'use client';

import type { FC } from 'react';
import { ScreenContent, ScreenFixedTop, ScreenTabBar } from '../../screen';
import { GameTile } from '../../components/GameTile';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useProfileData } from '../../queries/useProfileQueries';
import { useRushMidConfig } from '../../queries/useRushMidConfig';
import type { ScreenParams } from '../../types/navigation';
import rushMidHero from '../../assets/games/rush-mid-hero.webp';
import rouletteHero from '../../assets/games/roulette-hero.webp';
import caseBattleHero from '../../assets/games/case-battles-hero.webp';
import contractsHero from '../../assets/games/contracts-hero.webp';
import upgradeHero from '../../assets/games/upgrade-hero.webp';
import shopHero from '../../assets/games/shop-hero.webp';
import translations from './GamesScreen.translations';

interface GamesScreenProps {
  params?: ScreenParams;
  onBack?: () => void;
  isPreview?: boolean;
}

export const GamesScreen: FC<GamesScreenProps> = () => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const profile = useProfileData();
  const rushMid = useRushMidConfig();

  const games = [
    {
      id: 'case-battles',
      title: t('caseBattlesTitle'),
      desc: t('caseBattlesDescription'),
      cta: t('caseBattlesCta'),
      hero: caseBattleHero,
      accent: 'bg-game-case-battles',
      screen: 'case-battles' as const,
      active: profile?.caseBattlesRestrictedMode ? profile.skinsExchangeEnabled : true,
      comingSoon: profile?.caseBattlesRestrictedMode ? !profile.skinsExchangeEnabled : false,
      maintenance: false,
    },
    {
      id: 'upgrade',
      title: t('upgradeTitle'),
      desc: t('upgradeDescription'),
      cta: t('upgradeCta'),
      hero: upgradeHero,
      accent: 'bg-game-upgrade',
      screen: 'upgrade' as const,
      active: true,
      comingSoon: false,
      maintenance: false,
    },
    {
      id: 'rush-mid',
      title: t('rushMidTitle'),
      desc: rushMid.comingSoon ? '???' : t('rushMidDescription'),
      cta: t('rushMidCta'),
      hero: rushMidHero,
      accent: 'bg-game-rush-mid',
      screen: 'rush-mid' as const,
      active: !rushMid.comingSoon && !rushMid.maintenance,
      comingSoon: rushMid.comingSoon,
      maintenance: !rushMid.comingSoon && rushMid.maintenance,
    },
    {
      id: 'roulette',
      title: t('rouletteTitle'),
      desc: t('rouletteDescription'),
      cta: t('rouletteCta'),
      hero: rouletteHero,
      accent: 'bg-game-roulette',
      screen: 'roulette' as const,
      active: profile?.rouletteRestrictedMode ? profile.skinsExchangeEnabled : true,
      comingSoon: profile?.rouletteRestrictedMode ? !profile.skinsExchangeEnabled : false,
      maintenance: false,
    },
    {
      id: 'contracts',
      title: t('contractsTitle'),
      desc: t('contractsDescription'),
      cta: t('contractsCta'),
      hero: contractsHero,
      accent: 'bg-game-contracts',
      screen: 'contracts' as const,
      active: true,
      comingSoon: false,
      maintenance: false,
    },
    {
      id: 'shop',
      title: t('shopTitle'),
      desc: t('shopDescription'),
      cta: t('shopCta'),
      hero: shopHero,
      accent: 'bg-game-shop',
      screen: 'shop' as const,
      active: true,
      comingSoon: false,
      maintenance: false,
    },
  ];

  return (
    <>
      <ScreenFixedTop title={t('title')} />
      <ScreenTabBar />
      <ScreenContent scrollKey="games">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            paddingTop: 16,
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          {games.map((game) => (
            <GameTile
              key={game.id}
              title={game.title}
              description={game.desc}
              cta={game.cta}
              hero={game.hero}
              accent={game.accent}
              badge={game.comingSoon ? t('comingSoon') : game.maintenance ? t('maintenanceBadge') : null}
              onClick={game.active ? () => navigation.pushScreen('games', game.screen) : undefined}
            />
          ))}
        </div>
      </ScreenContent>
    </>
  );
};
